import React, { useState, useEffect } from 'react'
import { Upload, FileText, Check, X, Clock, AlertCircle, Calendar, Download, School, Building2, Search, Filter, ChevronDown, Eye } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'

interface StagedEvent {
  id: string
  school_id?: string
  district_id?: string
  school_name?: string
  district_name?: string
  event_type: string
  event_name: string
  event_date: string
  uploaded_by: string
  uploaded_at: string
  raw_text: string
  status: 'pending' | 'approved' | 'rejected'
}

interface School {
  id: string
  name: string
  district_id?: string
  district_name?: string
}

interface District {
  id: string
  name: string
}

export const CalendarUploadManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upload' | 'review'>('upload')
  const [schools, setSchools] = useState<School[]>([])
  const [districts, setDistricts] = useState<District[]>([])
  const [selectedSchool, setSelectedSchool] = useState<string>('')
  const [selectedDistrict, setSelectedDistrict] = useState<string>('')
  const [uploadType, setUploadType] = useState<'school' | 'district'>('school')
  const [file, setFile] = useState<File | null>(null)
  const [metadata, setMetadata] = useState({
    source: '',
    notes: '',
    school_year: new Date().getFullYear().toString()
  })
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<any>(null)

  // Review tab state
  const [stagedEvents, setStagedEvents] = useState<StagedEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set())
  const [filterType, setFilterType] = useState<'all' | 'school' | 'district'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadSchools()
    loadDistricts()
    if (activeTab === 'review') {
      loadStagedEvents()
    }
  }, [activeTab])

  const loadSchools = async () => {
    try {
      const data = await apiClient.get('/api/admin/schools')
      setSchools(data.schools || [])
    } catch (error) {
      console.error('Failed to load schools:', error)
      toast.error('Failed to load schools')
    }
  }

  const loadDistricts = async () => {
    try {
      const data = await apiClient.get('/api/admin/districts')
      setDistricts(data.districts || [])
    } catch (error) {
      console.error('Failed to load districts:', error)
      toast.error('Failed to load districts')
    }
  }

  const loadStagedEvents = async () => {
    setLoading(true)
    try {
      const events: StagedEvent[] = []

      // Load staged events for all schools
      for (const school of schools) {
        try {
          const data = await apiClient.get(`/api/admin/schools/${school.id}/staged-events`)
          const schoolEvents = (data.events || []).map((e: any) => ({
            ...e,
            school_id: school.id,
            school_name: school.name
          }))
          events.push(...schoolEvents)
        } catch (error) {
          console.error(`Failed to load events for school ${school.name}:`, error)
        }
      }

      // Load staged events for all districts
      for (const district of districts) {
        try {
          const data = await apiClient.get(`/api/admin/districts/${district.id}/staged-events`)
          const districtEvents = (data.events || []).map((e: any) => ({
            ...e,
            district_id: district.id,
            district_name: district.name
          }))
          events.push(...districtEvents)
        } catch (error) {
          console.error(`Failed to load events for district ${district.name}:`, error)
        }
      }

      setStagedEvents(events)
    } catch (error) {
      console.error('Failed to load staged events:', error)
      toast.error('Failed to load staged events')
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Please select a PDF file')
        return
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB')
        return
      }
      setFile(selectedFile)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a PDF file')
      return
    }

    if (uploadType === 'school' && !selectedSchool) {
      toast.error('Please select a school')
      return
    }

    if (uploadType === 'district' && !selectedDistrict) {
      toast.error('Please select a district')
      return
    }

    setUploading(true)
    setUploadResult(null)

    try {
      const formData = new FormData()
      formData.append('calendar_pdf', file)
      formData.append('source', metadata.source || `${file.name}`)
      formData.append('notes', metadata.notes)
      formData.append('school_year', metadata.school_year)

      const endpoint = uploadType === 'school'
        ? `/api/admin/schools/${selectedSchool}/upload-calendar`
        : `/api/admin/districts/${selectedDistrict}/upload-calendar`

      const response = await fetch(`/api${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error('Upload failed')
      }

      const result = await response.json()
      setUploadResult(result)
      toast.success(`PDF processed successfully! Found ${result.suggested_events_count} potential events`)

      // Reset form
      setFile(null)
      setMetadata({ source: '', notes: '', school_year: new Date().getFullYear().toString() })

      // Reload staged events
      await loadStagedEvents()
    } catch (error) {
      console.error('Upload error:', error)
      toast.error('Failed to upload calendar PDF')
    } finally {
      setUploading(false)
    }
  }

  const handleApprove = async (eventId: string) => {
    try {
      await apiClient.post(`/api/admin/staged-events/${eventId}/approve`, {})
      toast.success('Event approved')
      await loadStagedEvents()
    } catch (error) {
      console.error('Failed to approve event:', error)
      toast.error('Failed to approve event')
    }
  }

  const handleReject = async (eventId: string, reason: string) => {
    try {
      await apiClient.post(`/api/admin/staged-events/${eventId}/reject`, {
        rejection_reason: reason
      })
      toast.success('Event rejected')
      await loadStagedEvents()
    } catch (error) {
      console.error('Failed to reject event:', error)
      toast.error('Failed to reject event')
    }
  }

  const handleBulkApprove = async () => {
    if (selectedEvents.size === 0) {
      toast.error('No events selected')
      return
    }

    try {
      await apiClient.post('/api/admin/staged-events/bulk-approve', {
        event_ids: Array.from(selectedEvents)
      })
      toast.success(`Approved ${selectedEvents.size} events`)
      setSelectedEvents(new Set())
      await loadStagedEvents()
    } catch (error) {
      console.error('Failed to bulk approve:', error)
      toast.error('Failed to approve selected events')
    }
  }

  const toggleEventSelection = (eventId: string) => {
    const newSelection = new Set(selectedEvents)
    if (newSelection.has(eventId)) {
      newSelection.delete(eventId)
    } else {
      newSelection.add(eventId)
    }
    setSelectedEvents(newSelection)
  }

  const filteredEvents = stagedEvents.filter(event => {
    // Filter by type
    if (filterType === 'school' && !event.school_id) return false
    if (filterType === 'district' && !event.district_id) return false

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        event.event_name.toLowerCase().includes(query) ||
        event.school_name?.toLowerCase().includes(query) ||
        event.district_name?.toLowerCase().includes(query) ||
        event.event_type.toLowerCase().includes(query)
      )
    }

    return true
  }).filter(event => event.status === 'pending')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Calendar Upload Manager</h2>
        <p className="text-gray-600">Upload school/district calendars and review staged events</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('upload')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Upload className="w-4 h-4 inline-block mr-2" />
            Upload PDF
          </button>
          <button
            onClick={() => setActiveTab('review')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'review'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Eye className="w-4 h-4 inline-block mr-2" />
            Review Events
            {stagedEvents.filter(e => e.status === 'pending').length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                {stagedEvents.filter(e => e.status === 'pending').length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Upload Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Type
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setUploadType('school')}
                className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-colors ${
                  uploadType === 'school'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                <School className="w-5 h-5 inline-block mr-2" />
                School Calendar
              </button>
              <button
                onClick={() => setUploadType('district')}
                className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-colors ${
                  uploadType === 'district'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                <Building2 className="w-5 h-5 inline-block mr-2" />
                District Calendar
              </button>
            </div>
          </div>

          {/* School/District Selection */}
          {uploadType === 'school' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select School
              </label>
              <select
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a school...</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.name} {school.district_name && `(${school.district_name})`}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select District
              </label>
              <select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a district...</option>
                {districts.map(district => (
                  <option key={district.id} value={district.id}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* PDF File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Calendar PDF
            </label>
            <div className="flex items-center space-x-3">
              <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                <Upload className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">
                  {file ? file.name : 'Choose PDF file or drag here'}
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              {file && (
                <button
                  onClick={() => setFile(null)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Max file size: 10MB</p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source (optional)
              </label>
              <input
                type="text"
                value={metadata.source}
                onChange={(e) => setMetadata({ ...metadata, source: e.target.value })}
                placeholder="e.g., School website, District office"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                School Year
              </label>
              <input
                type="text"
                value={metadata.school_year}
                onChange={(e) => setMetadata({ ...metadata, school_year: e.target.value })}
                placeholder="2024-2025"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={metadata.notes}
              onChange={(e) => setMetadata({ ...metadata, notes: e.target.value })}
              rows={3}
              placeholder="Any additional notes about this calendar upload..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Upload Button */}
          <div>
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing PDF...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Upload className="w-5 h-5 mr-2" />
                  Upload and Process Calendar
                </span>
              )}
            </button>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start">
                <Check className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-900 mb-2">
                    Calendar Processed Successfully
                  </h4>
                  <p className="text-sm text-green-700 mb-3">
                    Found {uploadResult.suggested_events_count} potential events. Events have been added to the staging area for review.
                  </p>
                  <button
                    onClick={() => setActiveTab('review')}
                    className="text-sm text-green-700 font-medium hover:text-green-800 underline"
                  >
                    Review Staged Events →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Review Tab */}
      {activeTab === 'review' && (
        <div className="space-y-4">
          {/* Filters and Actions */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Filter */}
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Events</option>
                  <option value="school">School Events</option>
                  <option value="district">District Events</option>
                </select>
              </div>

              {/* Bulk Actions */}
              {selectedEvents.size > 0 && (
                <button
                  onClick={handleBulkApprove}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  <Check className="w-4 h-4 inline-block mr-2" />
                  Approve {selectedEvents.size} Selected
                </button>
              )}
            </div>
          </div>

          {/* Events List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Events</h3>
              <p className="text-gray-600">
                {searchQuery ? 'No events match your search criteria' : 'Upload a calendar to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEvents.map(event => (
                <StagedEventCard
                  key={event.id}
                  event={event}
                  selected={selectedEvents.has(event.id)}
                  onToggleSelect={() => toggleEventSelection(event.id)}
                  onApprove={() => handleApprove(event.id)}
                  onReject={(reason) => handleReject(event.id, reason)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Staged Event Card Component
const StagedEventCard: React.FC<{
  event: StagedEvent
  selected: boolean
  onToggleSelect: () => void
  onApprove: () => void
  onReject: (reason: string) => void
}> = ({ event, selected, onToggleSelect, onApprove, onReject }) => {
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }
    onReject(rejectReason)
    setShowRejectDialog(false)
    setRejectReason('')
  }

  return (
    <div className={`bg-white rounded-lg shadow p-4 border-2 transition-colors ${
      selected ? 'border-blue-500' : 'border-transparent'
    }`}>
      <div className="flex items-start space-x-4">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
        />

        {/* Event Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="text-lg font-medium text-gray-900">{event.event_name}</h4>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm text-gray-600">
                  {event.school_name ? (
                    <><School className="w-3 h-3 inline-block mr-1" />{event.school_name}</>
                  ) : (
                    <><Building2 className="w-3 h-3 inline-block mr-1" />{event.district_name}</>
                  )}
                </span>
                <span className="text-gray-300">•</span>
                <span className="text-sm text-gray-600">
                  <Calendar className="w-3 h-3 inline-block mr-1" />
                  {format(parseISO(event.event_date), 'MMM d, yyyy')}
                </span>
                <span className="text-gray-300">•</span>
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                  {event.event_type}
                </span>
              </div>
            </div>
          </div>

          {/* Raw Text Preview */}
          <details className="mt-2">
            <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
              View extracted text
            </summary>
            <div className="mt-2 p-3 bg-gray-50 rounded text-sm text-gray-700 font-mono">
              {event.raw_text}
            </div>
          </details>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          <button
            onClick={onApprove}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Approve"
          >
            <Check className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowRejectDialog(true)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Reject"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Event</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this event:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason for rejection..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              autoFocus
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectDialog(false)
                  setRejectReason('')
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reject Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
